import DetailsPageTitle from "components/common/DetailsPageTitle";
import NoDataFound from "components/common/NoDataFound";
import Spinners from "components/common/Spinners";
import { spaceTypeOptions } from "components/constants/DropdownOptions";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { getClosureDetails } from "store/actions/closureSlice";
import RadioCircle from "assets/images/icons/radioCircle.svg";
import { Lock, LockOpen, MapPin } from "lucide-react";
import { formatDate, getTimePart } from "components/common/Utils";
const ClosureDetails = () => {
  const { t } = useTranslation();
  document.title = t("Closure Details | Work Space");

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, isLoading, closureDetails } = useSelector(
    (state) => state.Closures
  );

  const [showSpaceDetails, setShowSpaceDetails] = useState(false);

  useEffect(() => {
    const getDetails = () => {
      const payload = {
        id,
      };
      dispatch(getClosureDetails(payload));
    };
    getDetails();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content common_details_page workshop_details_page closure_details">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle title={t("Closure Details")} />
            </Row>
            <div className="common_wrapper">
              <Row>
                <Col lg={7}>
                  {!closureDetails?.hasClosedEntireBranch && (
                    <Row>
                      {/* IMAGES ROW */}
                      <Col lg={12} className="images_section">
                        <div className="d-flex gap-3">
                          <div className="left_section">
                            {closureDetails?.spaceId?.[0]?.imageUrls?.[0] && (
                              <img
                                src={
                                  closureDetails?.spaceId?.[0]?.imageUrls?.[0]
                                }
                                alt="Main Image"
                              />
                            )}
                          </div>
                          <div className="right_section d-flex flex-column gap-3">
                            {closureDetails?.spaceId?.[0]?.imageUrls
                              ?.slice(1, 3) // Select the second and third images
                              .map((url, index) => (
                                <img
                                  key={index}
                                  src={url}
                                  alt={`Workspace Image ${index + 2}`}
                                />
                              ))}
                          </div>
                        </div>
                      </Col>

                      {/* TITLE ROW */}
                      <Col lg={12} className="mt_20">
                        <div className="g_between">
                          <h2 className="fs_24">
                            {spaceTypeOptions?.find(
                              (option) =>
                                option?.value === closureDetails.spaceType
                            )?.name || "-"}
                          </h2>
                          <button
                            className="main_btn px-3"
                            onClick={() =>
                              setShowSpaceDetails(!showSpaceDetails)
                            }
                          >
                            {showSpaceDetails ? t("Hide") : t("Show")}{" "}
                            {t("Space Details")}
                          </button>
                        </div>
                        <p className="text_gray fs_14">
                          <MapPin
                            className="text_gray me-1"
                            width={14}
                            height={14}
                          />
                          {closureDetails?.address}
                        </p>
                      </Col>
                    </Row>
                  )}
                  {/* SPACE DETAILS */}
                  {showSpaceDetails && (
                    <Row>
                      {closureDetails?.spaceId?.length === 0 ? (
                        <NoDataFound />
                      ) : (
                        closureDetails?.spaceId?.map((item) => (
                          <Col lg={6}>
                            <div className="single_room_inner g_between">
                              <div className="left d-flex align-items-center gap-3">
                                <img
                                  src={item?.imageUrls?.[0] || ""}
                                  alt=""
                                  width={59}
                                  height={59}
                                />
                                <div>
                                  <h2>{item?.name || "-"}</h2>
                                  <p>
                                    {item?.cityId?.name}, {item?.branchId?.name}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </Col>
                        ))
                      )}
                    </Row>
                  )}
                  <Row>
                    <h2 className="fs_20 mt_20">{t("Description")}</h2>
                    <Col lg={12}>
                      <div className="description_section">
                        <p className="fs_14">
                          {closureDetails?.description || "-"}
                        </p>
                        <p className="fs_14 mt-4 rtl">
                          {closureDetails?.descriptionAr || "-"}
                        </p>
                      </div>
                    </Col>
                  </Row>
                </Col>
                <Col lg={5} className="ps-4">
                  <h2 className="fs_20">{t("Closure Type")}</h2>
                  <Row>
                    <Col lg={12}>
                      <div className="description_section closure_type_div">
                        <div className="d-flex align-items-center gap-2 ">
                          <img
                            src={RadioCircle}
                            alt=""
                            width={15}
                            height={15}
                          />
                          <h2 className="fs_14 mt-1">
                            {closureDetails.type === 1
                              ? t("One-Time")
                              : t("Recurring")}{" "}
                          </h2>
                        </div>
                        {closureDetails.type === 2 && (
                          <div className="mt-3 recurrence_pattern">
                            <div className="d-flex gap-3">
                              <h2 className="fs_14 w_fit_content">
                                {t("Recurrence Pattern")}:
                              </h2>
                              <div>
                                {closureDetails?.dateAndTimes?.daysOfRecurrence?.map(
                                  (day) => (
                                    <span className="recurring_day" key={day}>
                                      {t("Recurring Every")} {day}
                                    </span>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </Col>
                    <h2 className="fs_20 mt-4">{t("Closure Start on")}</h2>
                    <Col lg={12}>
                      <div className="description_section date_time_div">
                        {closureDetails.type === 1 && (
                          <h2 className="fs_16 mb-2">
                            {formatDate(
                              closureDetails?.dateAndTimes?.startDate
                            )}
                          </h2>
                        )}
                        <div className="d-flex align-items-center gap-4">
                          <Lock className="text_dark_gray" size={30} />
                          <div className="d-flex gap-3">
                            <span className="time">
                              {getTimePart(
                                closureDetails?.dateAndTimes?.startTime,
                                "h"
                              )}
                            </span>
                            <span className="separator">:</span>
                            <span className="time">
                              {getTimePart(
                                closureDetails?.dateAndTimes?.startTime,
                                "mm"
                              )}
                            </span>
                            <span className="period">
                              {getTimePart(
                                closureDetails?.dateAndTimes?.startTime,
                                "a"
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <h2 className="fs_20 mt-4">{t("Closure Ends on")}</h2>
                    <Col lg={12}>
                      <div className="description_section date_time_div end_time">
                        {closureDetails.type === 1 && (
                          <h2 className="fs_16 mb-2">
                            {formatDate(closureDetails?.dateAndTimes?.endDate)}
                          </h2>
                        )}
                        <div className="d-flex align-items-center gap-4">
                          <LockOpen className="text_dark_gray" size={30} />
                          <div className="d-flex gap-3">
                            <span className="time">
                              {getTimePart(
                                closureDetails?.dateAndTimes?.endTime,
                                "h"
                              )}
                            </span>
                            <span className="separator">:</span>
                            <span className="time">
                              {getTimePart(
                                closureDetails?.dateAndTimes?.endTime,
                                "mm"
                              )}
                            </span>
                            <span className="period">
                              {getTimePart(
                                closureDetails?.dateAndTimes?.endTime,
                                "a"
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
            <Row>
              <div className="g_end mt_32 mb-2">
                <button
                  className="main_btn"
                  onClick={() => {
                    navigate(
                      `/management/ws-management/update-closure?id=${id}`
                    );
                  }}
                >
                  {t("Edit")}
                </button>
              </div>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default ClosureDetails;
